import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cookie, X } from "lucide-react";

const COOKIE_CONSENT_KEY = "aude-rights-cookie-consent";

export type CookieConsentValue = "accepted" | "refused" | null;

export const getCookieConsent = (): CookieConsentValue => {
  if (typeof window === "undefined") return null;
  const value = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (value === "accepted" || value === "refused") return value;
  return null;
};

export const setCookieConsent = (value: "accepted" | "refused") => {
  localStorage.setItem(COOKIE_CONSENT_KEY, value);
  // Dispatch event so GoogleAnalytics can react
  window.dispatchEvent(new CustomEvent("cookieConsentChanged", { detail: value }));
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if consent has already been given
    const consent = getCookieConsent();
    if (!consent) {
      // Small delay before showing banner for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
        setTimeout(() => setIsAnimating(true), 50);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    setCookieConsent("accepted");
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  const handleRefuse = () => {
    setCookieConsent("refused");
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 transition-all duration-300 ${
        isAnimating ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
      }`}
    >
      <div className="container mx-auto max-w-3xl">
        <div className="bg-card border border-border rounded-2xl shadow-xl p-5 md:p-6">
          <div className="flex flex-col gap-4">
            {/* Header with icon */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Cookie className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-semibold text-foreground text-lg">
                Enhance your experience
              </h3>
            </div>

            {/* Text */}
            <p className="text-sm text-muted-foreground leading-relaxed">
              We use analytics cookies to understand how you use our site and improve it for everyone. 
              Your privacy matters – no personal data is shared.{" "}
              <Link to="/cookies" className="text-primary hover:underline">
                Cookie policy
              </Link>
            </p>

            {/* Buttons - Accept very prominent, continue without is minimal */}
            <div className="flex flex-col gap-3 mt-1">
              <Button
                size="lg"
                onClick={handleAccept}
                className="w-full font-medium text-base py-6"
              >
                ✓ Accept & continue
              </Button>
              <button
                onClick={handleRefuse}
                className="text-[11px] text-muted-foreground/60 hover:text-muted-foreground transition-colors py-1 self-center"
              >
                continue without accepting
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
